/**
 * CR136: Production Tools Page
 * CR210: Added Public Deal Retention Policy controls
 * Provides admin-only tools for resetting production data
 */

import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Trash2, Database, Eye, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { PublicDealRetentionCard } from "@/components/admin/production-tools/PublicDealRetentionCard";
import { RolloutStrategyCard } from "@/components/admin/production-tools/RolloutStrategyCard";

interface DryRunResult {
  success: boolean;
  dry_run: boolean;
  production_deals_deleted: number;
  audit_log_deleted: number;
  site_state_reset: boolean;
  message?: string;
  error?: string;
}

interface SiteStatePreview {
  admin_snapshot_id: string | null;
  admin_batch_run_id: string | null;
  admin_deal_count: number | null;
  active_snapshot_id: string | null;
  active_batch_run_id: string | null;
  published_deal_count: number | null;
}

export default function ProductionTools() {
  const [loading, setLoading] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);
  const [siteState, setSiteState] = useState<SiteStatePreview | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [clearAuditLog, setClearAuditLog] = useState(false);
  const [featureEnabled, setFeatureEnabled] = useState<boolean | null>(null);

  const fetchSiteState = async () => {
    const { data, error } = await supabase.functions.invoke("site-state", {
      method: "GET",
    });

    if (!error && data) {
      setSiteState({
        admin_snapshot_id: data.admin_snapshot_id,
        admin_batch_run_id: data.admin_batch_run_id,
        admin_deal_count: data.admin_deal_count,
        active_snapshot_id: data.active_snapshot_id,
        active_batch_run_id: data.active_batch_run_id,
        published_deal_count: data.published_deal_count,
      });
    }
  };

  const runDryRun = async () => {
    setLoading(true);
    setDryRunResult(null);
    
    try {
      const { data, error } = await supabase.functions.invoke<DryRunResult>(
        "admin-reset-production",
        {
          body: {
            confirmation_text: "RESET PRODUCTION",
            dry_run: true,
            clear_audit_log: clearAuditLog,
          },
        }
      );

      if (error) {
        if (error.message?.includes("FEATURE_DISABLED")) {
          setFeatureEnabled(false);
          toast({
            title: "Feature Disabled",
            description: "Production reset is disabled. Set ALLOW_PROD_RESET=true to enable.",
            variant: "destructive",
          });
        } else {
          throw error;
        }
        return;
      }

      setFeatureEnabled(true);
      setDryRunResult(data);
    } catch (error) {
      console.error("Dry run failed:", error);
      toast({
        title: "Dry Run Failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const executeReset = async () => {
    if (confirmText !== "RESET PRODUCTION") {
      toast({
        title: "Confirmation Failed",
        description: "Type 'RESET PRODUCTION' to confirm",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setConfirmDialogOpen(false);

    try {
      const { data, error } = await supabase.functions.invoke<DryRunResult>(
        "admin-reset-production",
        {
          body: {
            confirmation_text: "RESET PRODUCTION",
            dry_run: false,
            clear_audit_log: clearAuditLog,
          },
        }
      );

      if (error) throw error;

      toast({
        title: "Production Reset Complete",
        description: data?.message || "All production data has been cleared",
      });

      // Refresh data
      setDryRunResult(null);
      setConfirmText("");
      await fetchSiteState();
      await runDryRun();
    } catch (error) {
      console.error("Reset failed:", error);
      toast({
        title: "Reset Failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSiteState();
    runDryRun();
  }, []);

  return (
    <AdminLayout>
      <div className="p-6 space-y-6 max-w-4xl">
        <div className="flex items-center gap-3">
          <Database className="h-8 w-8 text-destructive" />
          <div>
            <h1 className="text-2xl font-bold">Production Tools</h1>
            <p className="text-muted-foreground">
              Admin-only tools for managing production data
            </p>
          </div>
        </div>

        {/* Current State Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Current Site State
            </CardTitle>
            <CardDescription>
              Current production and admin snapshot configuration
            </CardDescription>
          </CardHeader>
          <CardContent>
            {siteState ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground">Admin Snapshot</h4>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">ID:</span>
                      <code className="text-xs bg-muted px-1 rounded">
                        {siteState.admin_snapshot_id || "—"}
                      </code>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Deals:</span>
                      <Badge variant="secondary">{siteState.admin_deal_count ?? 0}</Badge>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground">Live (Public)</h4>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">ID:</span>
                      <code className="text-xs bg-muted px-1 rounded">
                        {siteState.active_snapshot_id || "—"}
                      </code>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Deals:</span>
                      <Badge variant="secondary">{siteState.published_deal_count ?? 0}</Badge>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              className="mt-4"
              onClick={() => {
                fetchSiteState();
                runDryRun();
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </CardContent>
        </Card>

        {/* CR210: Public Deal Retention Policy Card */}
        <PublicDealRetentionCard />

        {/* Reset Production Card */}
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Reset Production
            </CardTitle>
            <CardDescription>
              Permanently delete all production deals and reset site state.
              <strong className="block mt-1">This action cannot be undone.</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Feature Status */}
            {featureEnabled === false && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Production reset is disabled. Set <code className="bg-muted px-1 rounded">ALLOW_PROD_RESET=true</code> in secrets to enable.
                </p>
              </div>
            )}

            {/* Dry Run Results */}
            {dryRunResult && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Preview (Dry Run)
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Production Deals:</span>
                    <Badge variant="destructive" className="ml-2">
                      {dryRunResult.production_deals_deleted} to delete
                    </Badge>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Audit Log Entries:</span>
                    <Badge variant={clearAuditLog ? "destructive" : "secondary"} className="ml-2">
                      {clearAuditLog ? `${dryRunResult.audit_log_deleted} to delete` : "Keep"}
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            {/* Options */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="clearAuditLog" 
                  checked={clearAuditLog}
                  onCheckedChange={(checked) => {
                    setClearAuditLog(checked === true);
                    // Re-run dry run with new setting
                    setTimeout(() => runDryRun(), 100);
                  }}
                />
                <Label htmlFor="clearAuditLog" className="text-sm">
                  Also clear publish audit log
                </Label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={runDryRun}
                disabled={loading}
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview Changes
              </Button>
              <Button 
                variant="destructive"
                onClick={() => setConfirmDialogOpen(true)}
                disabled={loading || featureEnabled === false || !dryRunResult}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Reset Production
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Confirm Production Reset
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-4">
                <p>
                  This will permanently delete:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>{dryRunResult?.production_deals_deleted || 0}</strong> production deals</li>
                  {clearAuditLog && (
                    <li><strong>{dryRunResult?.audit_log_deleted || 0}</strong> audit log entries</li>
                  )}
                  <li>All admin and live snapshot references</li>
                </ul>
                <div className="pt-4">
                  <Label htmlFor="confirmInput" className="text-sm font-medium">
                    Type <code className="bg-muted px-1 rounded">RESET PRODUCTION</code> to confirm:
                  </Label>
                  <Input
                    id="confirmInput"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="RESET PRODUCTION"
                    className="mt-2"
                    autoComplete="off"
                  />
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmText("")}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={executeReset}
                disabled={confirmText !== "RESET PRODUCTION" || loading}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {loading ? "Resetting..." : "Reset Production"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* CR320: Rollout Strategy */}
        <RolloutStrategyCard />
      </div>
    </AdminLayout>
  );
}
